import { useState, useEffect, useRef, RefObject } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { ThunkDispatch } from '@reduxjs/toolkit'
import { editPhotoActions } from '../../../reducers/editPhoto'
import { selectBeforeImageData } from '../../../selectors'

interface UseStageSetupReturn {
    stageWrapRef: RefObject<HTMLDivElement>
    stageRef: RefObject<any>
    outputStageRef: RefObject<any>
    previewCanvasRef: RefObject<HTMLCanvasElement>
    stageWidth: number
    stageHeight: number
    outputStageWidth: number
    outputStageHeight: number
}

/**
 * Stage, OutputStage, Canvas の初期化とサイズ管理を行うカスタムHook
 */
export function useStageSetup(): UseStageSetupReturn {
    const dispatch = useDispatch<ThunkDispatch<any, any, any>>()
    const stageWrapRef = useRef<HTMLDivElement>(null)
    const stageRef = useRef<any>(null)
    const outputStageRef = useRef<any>(null)
    const previewCanvasRef = useRef<HTMLCanvasElement>(null)

    const [stageWidth, setStageWidth] = useState(0)
    const [stageHeight, setStageHeight] = useState(0)
    const [outputStageWidth, setOutputStageWidth] = useState(0)
    const [outputStageHeight, setOutputStageHeight] = useState(0)

    const { stage, outputStage, previewCanvas, capturedAfterImageRef } = useSelector(
        (state: any) => state.editPhoto
    )
    const selectedBeforeImageData = useSelector((state: any) => selectBeforeImageData(state))

    // 編集中は capturedAfterImageRef を使用
    const afterImageRef = capturedAfterImageRef

    // Stage, OutputStage, Canvas の初期化
    // capturedAfterImageRef が設定されると Stage がレンダリングされるので、その時に初期化
    useEffect(() => {
        // capturedAfterImageRef がない場合は Stage がまだレンダリングされていない
        if (!capturedAfterImageRef) return

        const newStage = stageRef.current?.getStage()
        if (newStage && !stage) {
            dispatch(editPhotoActions.setStage(newStage))
        }

        const newOutputStage = outputStageRef.current?.getStage()
        if (newOutputStage && !outputStage) {
            dispatch(editPhotoActions.setOutputStage(newOutputStage))
        }

        const newPreviewCanvas = previewCanvasRef.current
        if (newPreviewCanvas && !previewCanvas) {
            dispatch(editPhotoActions.setPreviewCanvas(newPreviewCanvas))
        }
    }, [capturedAfterImageRef, stage, outputStage, previewCanvas, dispatch])

    // ステージサイズの計算と設定
    useEffect(() => {
        if (!stageWrapRef.current || !afterImageRef) return

        const maxStageHeight =
            window.innerHeight - 11 * parseInt(getComputedStyle(document.documentElement).fontSize)
        const maxStageWidth = stageWrapRef.current.clientWidth

        let newStageHeight = afterImageRef.height * (maxStageWidth / afterImageRef.width)
        let newStageWidth: number

        if (newStageHeight <= maxStageHeight) {
            newStageWidth = maxStageWidth
        } else {
            newStageHeight = maxStageHeight
            newStageWidth = afterImageRef.width * (maxStageHeight / afterImageRef.height)
        }

        setStageHeight(newStageHeight)
        setStageWidth(newStageWidth)
        stageWrapRef.current.style.height = newStageHeight + 'px'

        // 出力ステージのサイズ（元画像のサイズ）
        const outputWidth = afterImageRef.naturalWidth || afterImageRef.width
        const outputHeight = afterImageRef.naturalHeight || afterImageRef.height
        setOutputStageWidth(outputWidth)
        setOutputStageHeight(outputHeight)
    }, [selectedBeforeImageData, afterImageRef])

    return {
        stageWrapRef,
        stageRef,
        outputStageRef,
        previewCanvasRef,
        stageWidth,
        stageHeight,
        outputStageWidth,
        outputStageHeight,
    }
}
